import { ArrayHelpers, Field, FieldArray } from 'formik';
import React from 'react';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../../utils/spacing';
import Text from '../../../../v2BaseComponents/Text';
import { getIntl } from '../../../../../utils/intl';
import IconButton from '../../../../v2BaseComponents/IconButton';
import { useIsDisabledByParent } from '../../../../v2BaseComponents/IsDisabledProvider';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { required } from '../../../../../utils/validators';
import Divider from '../../../../v2BaseComponents/Divider';
import { OrganizationTypes } from '../../../../../primary-record-types';
import { v4 as uuid } from 'uuid';
import FormInputSection from '../FormInputSection';

const intl = getIntl('invitation-definition-form-fields-file-inputs', {
    fileInputs: 'Consent File Inputs',
    fileInputLabel: 'Consent File Input #{index}',
    fileInputName: 'File Input Name',
    fileInputDescription: 'File Input Description',
    isRequired: 'Is Required?',
});

export type FileInputsFieldOwnProps = {
    isDisabled: boolean;
    values: OrganizationTypes.InvitationDefinitionInputFile[];
};

export type FileInputsFieldProps = FileInputsFieldOwnProps;

const FileInputField: React.FC<{
    index: number;
    onRemove: () => void;
    isDisabled: boolean;
}> = (props) => {
    return (
        <FormInputSection
            title={intl('fileInputLabel', { index: props.index + 1 })}
            removeTestID={`remove-file-input-${props.index}`}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
        >
            <Field
                name={`fileInputs.${props.index}.name`}
                component={FormikInputs.Input}
                isRequired
                validate={required}
                title={intl('fileInputName')}
            />
            <Field
                name={`fileInputs.${props.index}.description`}
                component={FormikInputs.Input}
                isRequired
                validate={required}
                title={intl('fileInputDescription')}
            />
            <Field
                name={`fileInputs.${props.index}.isRequired`}
                component={FormikInputs.Switch}
                title={intl('isRequired')}
            />
        </FormInputSection>
    );
};

const FileInputsField: React.FC<FileInputsFieldProps> = (props) => {
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    return (
        <FieldArray name="fileInputs">
            {(arrayHelpers: ArrayHelpers<OrganizationTypes.InvitationDefinitionInputFile[]>) => (
                <Stack
                    vertical
                    space={contentStackSpacing}
                    fullWidth
                    style={{ marginTop: getSpacing(2.5), marginBottom: getSpacing(2.5) }}
                >
                    <Stack horizontal justify={'space-between'} align="center" fullWidth>
                        <Text variant="h5">{intl('fileInputs')}</Text>
                        <IconButton
                            testID="add-file-input"
                            icon="Plus"
                            variant="transparent"
                            onPress={() => {
                                const newValue: OrganizationTypes.InvitationDefinitionInputFile = {
                                    name: '',
                                    description: '',
                                    isRequired: false,
                                    type: 'file',
                                    fileInputId: uuid(),
                                };
                                arrayHelpers.push(newValue);
                            }}
                            isDisabled={isDisabled}
                        />
                    </Stack>
                    {props.values.map((image, index) => (
                        <FileInputField
                            key={index}
                            onRemove={() => arrayHelpers.remove(index)}
                            index={index}
                            isDisabled={props.isDisabled}
                        />
                    ))}
                    {props.values.length ? <Divider horizontal /> : null}
                </Stack>
            )}
        </FieldArray>
    );
};

export default React.memo(FileInputsField);
